import {
  File,
  FileText,
  Forward,
  Image as ImageIcon,
  Images,
  Link as LinkIcon,
  MapPin,
  MessageSquare,
  Mic,
  Phone,
  Sticker,
  Video
} from 'lucide-svelte';
import { detectPreviewType, getMediaUrlHints, hasLink, isPDF, isVideo } from '$lib/utils';

const TYPE_META = {
  album: { badge: 'Album', label: 'Bộ ảnh', color: 'sky', icon: Images },
  audio: { badge: 'Thoại', label: 'Tin nhắn thoại', color: 'sky', icon: Mic },
  image: { badge: 'Ảnh', label: 'Hình ảnh', color: 'sky', icon: ImageIcon },
  video: { badge: 'Video', label: 'Video', color: 'rose', icon: Video },
  pdf: { badge: 'PDF', label: 'Tài liệu PDF', color: 'amber', icon: FileText },
  file: { badge: 'Tệp', label: 'Tệp đính kèm', color: 'slate', icon: File },
  link: { badge: 'Link', label: 'Liên kết', color: 'emerald', icon: LinkIcon },
  location: { badge: 'Vị trí', label: 'Vị trí hiện tại', color: 'rose', icon: MapPin },
  share: { badge: 'Chia sẻ', label: 'Tin nhắn được chia sẻ', color: 'cyan', icon: Forward },
  call: { badge: 'Cuộc gọi', label: 'Nhật ký cuộc gọi', color: 'emerald', icon: Phone },
  sticker: { badge: 'Sticker', label: 'Sticker', color: 'violet', icon: Sticker },
  text: { badge: 'Tin nhắn', label: 'Tin nhắn văn bản', color: 'slate', icon: MessageSquare },
  empty: { badge: 'Trống', label: 'Chưa có nội dung', color: 'slate', icon: MessageSquare }
};

function safeParse(value) {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

function truncate(text = '', length = 72) {
  const normalized = String(text || '').replace(/\s+/g, ' ').trim();
  if (!normalized) return '';
  if (normalized.length <= length) return normalized;
  return `${normalized.slice(0, length - 1)}...`;
}

function formatCallSummary(payload = {}) {
  const typeLabel = payload.callType === 'audio' ? 'thoại' : 'video';
  const statusMap = {
    missed: 'Nhỡ máy',
    ended: 'Đã kết thúc',
    declined: 'Bị từ chối',
    busy: 'Máy bận'
  };

  const base = `Cuộc gọi ${typeLabel}`;
  const suffix = statusMap[payload.status] || 'Đã diễn ra';
  if (payload.duration > 0) {
    return `${base} · ${suffix} · ${payload.duration}s`;
  }
  return `${base} · ${suffix}`;
}

function normalizeSharedType(payload = {}) {
  const sharedType = payload.sharedType || payload.type || 'text';
  if (sharedType === 'media') {
    const message = payload.message || '';
    const mime = payload.mime || payload.meta?.mime || payload.media?.mime || '';
    if (isPDF(message)) return 'pdf';
    if (isVideo(message, mime)) return 'video';
    const detected = detectPreviewType(message);
    return detected === 'image' ? 'image' : detected === 'video' ? 'video' : detected === 'pdf' ? 'pdf' : 'file';
  }
  if (sharedType === 'message_share' || sharedType === 'share') return 'share';
  return sharedType;
}

function formatShareSummary(payload = {}) {
  const sharedMessages = Array.isArray(payload.messages) ? payload.messages : [];
  if (sharedMessages.length > 1) return `${sharedMessages.length} tin nhắn được chia sẻ`;

  const shared = sharedMessages[0] || payload;
  const sharedType = normalizeSharedType(shared);
  const sharedMessage = shared.message || '';

  if (sharedMessages.length === 1 && sharedType === 'share') return TYPE_META.share.label;
  if (sharedType === 'text') return truncate(sharedMessage || TYPE_META.share.label, 72);
  if (sharedType === 'call') return formatCallSummary(safeParse(sharedMessage) || shared.callData || {});
  if (sharedType === 'sticker') return 'Sticker được chia sẻ';
  if (sharedType === 'location') return TYPE_META.location.label;
  return describeText(sharedType, sharedMessage, {
    count: safeParse(sharedMessage)?.urls?.length || shared.urls?.length || 0,
    hintedName: shared.name || shared.fileName || ''
  }) || TYPE_META.share.label;
}

function describeText(type, rawText = '', extra = {}) {
  const text = String(rawText || '');
  const hintedName = String(extra.hintedName || '').trim();
  const isInternalUpload = /^\/?api\/upload\/files\/[^/?#]+/i.test(text);

  if (type === 'album') {
    const count = extra.count || 0;
    return count > 0 ? `${count} ảnh trong bộ sưu tập` : TYPE_META.album.label;
  }
  if (type === 'audio') {
    const seconds = Math.max(0, Math.round(Number(extra.duration) || 0));
    const time = seconds ? `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}` : '';
    return time ? `Tin nhắn thoại · ${time}` : TYPE_META.audio.label;
  }
  if (type === 'image' || type === 'video' || type === 'pdf' || type === 'file') {
    if (hintedName) return truncate(hintedName, 44);
    if (isInternalUpload) return TYPE_META[type].label;
    const parts = text.split('/');
    return truncate(parts[parts.length - 1] || TYPE_META[type].label, 44);
  }
  if (type === 'link') return truncate(text, 64);
  if (type === 'location') return TYPE_META.location.label;
  if (type === 'sticker') return 'Sticker đã được gửi';
  if (type === 'call') return formatCallSummary(extra.callData || {});
  if (type === 'share') return formatShareSummary(extra.shareData || safeParse(text) || {});
  return truncate(text || TYPE_META.text.label, 84);
}

function deriveTypeFromMessage(message = {}) {
  if (message.type === 'album') return 'album';
  if (message.type === 'audio') return 'audio';
  if (message.type === 'sticker') return 'sticker';
  if (message.type === 'call') return 'call';
  if (message.type === 'share') return 'share';
  if (message.type === 'location') return 'location';
  if (message.type === 'media') {
    if (isPDF(message.message)) return 'pdf';
    if (isVideo(message.message, message.meta?.mime || message.media?.mime || '')) return 'video';
    const detected = detectPreviewType(message.message);
    return detected === 'image' ? 'image' : detected === 'video' ? 'video' : detected === 'pdf' ? 'pdf' : 'file';
  }
  if (hasLink(message.message || '')) return 'link';
  return 'text';
}

function detectStructuredPayload(input) {
  const parsed = typeof input === 'string' ? safeParse(input) : input;
  if (!parsed || typeof parsed !== 'object') return null;

  if (parsed.fromConversationId || parsed.sharedType) {
    return { kind: 'share', payload: parsed };
  }

  if (parsed.callType || parsed.status || typeof parsed.duration === 'number') {
    if (parsed.url && (String(parsed.mime || '').startsWith('audio/') || Array.isArray(parsed.waveform))) {
      return { kind: 'audio', payload: parsed };
    }
    return { kind: 'call', payload: parsed };
  }

  if (parsed.url && (String(parsed.mime || '').startsWith('audio/') || Array.isArray(parsed.waveform))) {
    return { kind: 'audio', payload: parsed };
  }

  if (Array.isArray(parsed.urls)) {
    return { kind: parsed.urls.length > 1 ? 'album' : 'image', payload: parsed };
  }

  if (Number.isFinite(parsed.lat) && Number.isFinite(parsed.lng)) {
    return { kind: 'location', payload: parsed };
  }

  return null;
}

export function getPreviewMeta(input, options = {}) {
  const fallbackText = options.fallbackText || 'Chưa có nội dung';
  const hints = typeof input === 'string' ? getMediaUrlHints(input) : { mime: '', name: '' };

  if (input && typeof input === 'object' && 'type' in input) {
    const type = deriveTypeFromMessage(input);
    const meta = TYPE_META[type] || TYPE_META.text;
    const albumData = input.type === 'album' ? safeParse(input.message) || input.message : null;
    const callData = input.type === 'call' ? safeParse(input.message) || {} : {};
    const shareData = input.type === 'share' ? safeParse(input.message) || {} : {};

    return {
      type,
      icon: meta.icon,
      badge: meta.badge,
      label: meta.label,
      color: meta.color,
      text: describeText(type, input.message, {
        count: albumData?.urls?.length || 0,
        callData,
        shareData,
        duration: type === 'audio' ? (safeParse(input.message)?.duration || input.duration) : 0
      }),
      secondary: type === 'share' ? 'Chuyển tiếp từ cuộc trò chuyện khác' : ''
    };
  }

  const structured = detectStructuredPayload(input);
  if (structured?.kind === 'audio') {
    const meta = TYPE_META.audio;
    return {
      type: 'audio',
      icon: meta.icon,
      badge: meta.badge,
      label: meta.label,
      color: meta.color,
      text: describeText('audio', '', { duration: structured.payload.duration }),
      secondary: ''
    };
  }
  if (structured?.kind === 'share') {
    const nestedType = structured.payload.messages?.length
      ? normalizeSharedType(structured.payload.messages[0])
      : structured.payload.sharedType === 'album'
      ? 'album'
      : structured.payload.sharedType === 'media'
        ? detectPreviewType(structured.payload.message)
        : structured.payload.sharedType || 'share';
    const childMeta = TYPE_META[nestedType] || TYPE_META.share;

    return {
      type: 'share',
      icon: TYPE_META.share.icon,
      badge: TYPE_META.share.badge,
      label: childMeta.label,
      color: 'cyan',
      text: formatShareSummary(structured.payload) || describeText(nestedType, structured.payload.message, { count: structured.payload.urls?.length || 0 }),
      secondary: structured.payload.note ? truncate(structured.payload.note, 72) : 'Từ cuộc trò chuyện khác'
    };
  }

  if (structured?.kind === 'call') {
    const meta = TYPE_META.call;
    return {
      type: 'call',
      icon: meta.icon,
      badge: meta.badge,
      label: meta.label,
      color: meta.color,
      text: describeText('call', '', { callData: structured.payload }),
      secondary: ''
    };
  }

  if (structured?.kind === 'album') {
    const meta = TYPE_META.album;
    return {
      type: 'album',
      icon: meta.icon,
      badge: meta.badge,
      label: meta.label,
      color: meta.color,
      text: describeText('album', '', { count: structured.payload.urls?.length || 0 }),
      secondary: ''
    };
  }

  if (structured?.kind === 'location') {
    const meta = TYPE_META.location;
    return {
      type: 'location',
      icon: meta.icon,
      badge: meta.badge,
      label: meta.label,
      color: meta.color,
      text: meta.label,
      secondary: ''
    };
  }

  const previewType = detectPreviewType(input);
  const type = previewType === 'gallery' ? 'album' : hasLink(String(input || '')) ? 'link' : previewType;
  const meta = TYPE_META[type] || TYPE_META.text;

  return {
    type,
    icon: meta.icon,
    badge: meta.badge,
    label: meta.label,
    color: meta.color,
    text: input ? describeText(type, input, { hintedName: hints.name, hintedMime: hints.mime }) : fallbackText,
    secondary: ''
  };
}
